import { useState, useEffect, useContext } from "react";
import {
    Form,
    Input,
    Button,
    Row,
    Col,
    message,
    Modal
} from "antd";
import { SendOutlined, PlusOutlined } from "@ant-design/icons";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const DEFAULT_TO = "allbashyamemployees@bashyamgroup.com";

export default function SendMail({ employee }) {
    const [form] = Form.useForm();
    const [modalForm] = Form.useForm();
    const [body, setBody] = useState("");
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [additionalData, setAdditionalData] = useState({
        experience_summary: "",
        extension_number: "",
        cug: "",
        email: ""
    });

    const { authData, backUrl } = useContext(AuthContext);

    const subject = employee
        ? `Welcome On Board - ${employee.initial} ${employee.first_name} ${employee.surname} ${employee.last_name}`
        : "";

    // 🔹 Build mail body dynamically
    const buildBody = (emp, extraData = additionalData) => {
        if (!emp) return "";

        const gender = emp.gender || "";
        const genderLower = gender.toLowerCase();
        const title = genderLower === "female" ? "Ms." : "Mr.";
        const pronoun = genderLower === "female" ? "her" : "him";
        const subjPronoun = genderLower === "female" ? "she" : "he";

        return `
<p>Dear Team,</p>

<!-- 🔹 CENTERED INITIAL DETAILS -->
<table width="100%" cellpadding="0" cellspacing="0" role="presentation"
       style="border-collapse:collapse; margin-bottom:20px;">
    <tr>
        <td align="center" style="padding:20px 0;">

            <!-- Profile Image -->
            ${emp.photo ? `<img
                src="${emp.photo}"
                alt="${employee.initial} ${employee.first_name} ${employee.surname} ${employee.last_name}"
                width="120"
                height="120"
                style="
                    display:block;
                    margin-bottom:15px;
                    border:none;
                    outline:none;
                    text-decoration:none;
                    border-radius:60px;
                    object-fit:cover;
                "
            />` : ''}

            <!-- Employee Details -->
            <table cellpadding="0" cellspacing="0" role="presentation"
                   style="border-collapse:collapse; text-align:left; margin:0 auto;">
                <tr>
                    <td style="padding:4px 8px;"><strong>Name</strong></td>
                    <td style="padding:4px 8px;">:</td>
                    <td style="padding:4px 8px;">${employee.initial} ${employee.first_name} ${employee.surname} ${employee.last_name}</td>
                </tr>
                <tr>
                    <td style="padding:4px 8px;"><strong>Job Title</strong></td>
                    <td style="padding:4px 8px;">:</td>
                    <td style="padding:4px 8px;">${emp.designation_name || 'N/A'}</td>
                </tr>
                <tr>
                    <td style="padding:4px 8px;"><strong>Reporting To</strong></td>
                    <td style="padding:4px 8px;">:</td>
                    <td style="padding:4px 8px;">${emp.reporting_to || "—"}</td>
                </tr>
                <tr>
                    <td style="padding:4px 8px;"><strong>Site / Location</strong></td>
                    <td style="padding:4px 8px;">:</td>
                    <td style="padding:4px 8px;">${emp.location || "Head Office"}</td>
                </tr>
                <tr>
                    <td style="padding:4px 8px;"><strong>Description of Role</strong></td>
                    <td style="padding:4px 8px;">:</td>
                    <td style="padding:4px 8px;">
                        ${emp.role_description || emp.designation_name || 'N/A'}
                    </td>
                </tr>
                <tr>
                    <td style="padding:4px 8px;"><strong>Date of Joining</strong></td>
                    <td style="padding:4px 8px;">:</td>
                    <td style="padding:4px 8px;">${emp.date_of_joining || 'N/A'}</td>
                </tr>
            </table>

        </td>
    </tr>
</table>

<p>
We are delighted to welcome <strong>${title} ${employee.initial} ${employee.first_name} ${employee.surname} ${employee.last_name}</strong> to
<strong>${emp.organisation_name || 'Bashyam Group'}</strong> as our new
<strong>${emp.designation_name || 'Team Member'}</strong>, effective
<strong>${emp.date_of_joining || 'soon'}</strong>.
</p>

${extraData.experience_summary ? `
<p>
${extraData.experience_summary}
</p>
` : `
<p>
${title} ${employee.initial} ${employee.first_name} ${employee.surname} ${employee.last_name} brings valuable experience and expertise that
will greatly strengthen our team and support our organizational goals.
</p>
`}

<p>
For any assistance, you may reach ${pronoun} at:
</p>

<p>
<strong>Landline Extension:</strong> ${extraData.extension_number || emp.landline || "N/A"}<br/>
<strong>CUG:</strong> ${extraData.cug || emp.cug || "N/A"}<br/>
<strong>Email:</strong> ${extraData.official_email_id|| "N/A"}
</p>

<p>
Let us all extend a warm welcome to
<strong>${title} ${employee.initial} ${employee.first_name} ${employee.surname} ${employee.last_name}</strong> and offer
${pronoun} our full support as
${subjPronoun} begins the journey with us.
</p>

<p>
<strong>Welcome on board, ${title} ${employee.initial} ${employee.first_name} ${employee.surname} ${employee.last_name}!</strong>
</p>

<p>
--<br/>
Regards,<br/>
<strong>HR Team</strong><br/>
${emp.organisation_name || 'Bashyam Group'}
</p>
`;
    };

    // 🔹 Initialize form & body when component mounts
    useEffect(() => {
        if (employee) {
            const mailBody = buildBody(employee);
            setBody(mailBody);

            form.setFieldsValue({
                to: DEFAULT_TO,
                subject: subject,
            });
        }
    }, [employee]);

    // 🔹 Handle modal open
    const handleAddData = () => {
        const currentValues = form.getFieldsValue();

        modalForm.setFieldsValue({
            to_emails: currentValues.to || DEFAULT_TO,
            cc_emails: currentValues.cc || "",
            experience_summary: additionalData.experience_summary,
            extension_number: additionalData.extension_number,
            cug: additionalData.cug,
            email: additionalData.email
        });
        setModalVisible(true);
    };

    // 🔹 Handle modal apply - Send announcement to backend
    const handleModalApply = async () => {
        try {
            const values = await modalForm.validateFields();

            if (!employee || !employee.id) {
                message.error("Employee data is missing");
                return;
            }

            // Update additional data state
            const updatedData = {
                experience_summary: values.experience_summary || "",
                extension_number: values.extension_number || "",
                cug: values.cug || "",
                email: values.email || ""
            };
            setAdditionalData(updatedData);

            // Rebuild body with new data
            const updatedBody = buildBody(employee, updatedData);
            setBody(updatedBody);

            // Update form fields
            form.setFieldsValue({
                to: values.to_emails || DEFAULT_TO,
                cc: values.cc_emails || ""
            });

            // Prepare payload for backend
            const payload = {
                employee_id: employee.id,
                preview: false,
                experience_summary: values.experience_summary || "",
                extension_number: values.extension_number || "",
                cug: values.cug || "",
                email: values.email || "",
                cc_emails: values.cc_emails || "",
                to_emails: values.to_emails || DEFAULT_TO
            };

            setLoading(true);

            // Send to backend
            const response = await axios.post(
                `${backUrl}/api/emp/send-announcement/`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );

            message.success("Announcement sent successfully!");
            setModalVisible(false);

        } catch (error) {
            if (error.name === 'ValidationError') {
                // Form validation failed
                console.error("Validation failed:", error);
            } else {
                // API error
                message.error(
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Failed to send announcement"
                );
                console.error("API Error:", error);
            }
        } finally {
            setLoading(false);
        }
    };

    if (!employee) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>No employee data available</p>
            </div>
        );
    }

    return (
        <>
            <Form
                form={form}
                layout="vertical"
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="To"
                            name="to"
                            rules={[
                                { required: true, message: "Please enter recipient email" },
                                { type: "email", message: "Please enter a valid email" }
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="CC"
                            name="cc"
                            rules={[
                                { type: "email", message: "Please enter a valid email" }
                            ]}
                        >
                            <Input placeholder="Optional" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label="Subject"
                    name="subject"
                    rules={[{ required: true, message: "Please enter subject" }]}
                >
                    <Input />
                </Form.Item>

                <div style={{ marginBottom: 16, display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={handleAddData}
                        style={{ borderColor: '#0e497b', color: '#0e497b' }}
                    >
                        Add Data & Send
                    </Button>
                </div>

                <Form.Item label="Message Preview">
                    <CKEditor
                        editor={ClassicEditor}
                        data={body}
                        onChange={(event, editor) => {
                            setBody(editor.getData());
                        }}
                        config={{
                            toolbar: [
                                'heading', '|',
                                'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                                'blockQuote', 'insertTable', '|',
                                'undo', 'redo'
                            ]
                        }}
                    />
                </Form.Item>
            </Form>

            {/* 🔹 ADD DATA MODAL */}
            <Modal
                title="Add Additional Data & Send Announcement"
                open={modalVisible}
                onOk={handleModalApply}
                onCancel={() => setModalVisible(false)}
                okText="Apply & Send"
                cancelText="Cancel"
                width={600}
                confirmLoading={loading}
                okButtonProps={{
                    style: { background: '#0e497b' },
                    loading: loading
                }}
            >
                <Form
                    form={modalForm}
                    layout="vertical"
                >
                    <Form.Item
                        label="To Emails"
                        name="to_emails"
                        rules={[
                            { required: true, message: "Please enter recipient email" },
                            { type: "email", message: "Please enter a valid email" }
                        ]}
                    >
                        <Input placeholder="e.g., allbashyamemployees@bashyamgroup.com" />
                    </Form.Item>

                    <Form.Item
                        label="CC Emails"
                        name="cc_emails"
                        rules={[
                            { type: "email", message: "Please enter a valid email" }
                        ]}
                    >
                        <Input placeholder="Optional CC emails" />
                    </Form.Item>

                    <Form.Item
                        label="Experience Summary"
                        name="experience_summary"
                        tooltip="Brief description of the employee's experience"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="e.g., Ms. Keshika brings extensive Python experience and has worked on multiple enterprise projects..."
                        />
                    </Form.Item>

                    <Form.Item
                        label="Extension Number"
                        name="extension_number"
                        tooltip="Landline extension number"
                    >
                        <Input placeholder="e.g., 1234" />
                    </Form.Item>

                    <Form.Item
                        label="CUG Number"
                        name="cug"
                        tooltip="Corporate mobile number"
                    >
                        <Input placeholder="e.g., 9876543210" />
                    </Form.Item>

                    <Form.Item
                        label="Email"
                        name="email"
                        tooltip="Official email address"
                        rules={[
                            { type: 'email', message: 'Please enter a valid email' }
                        ]}
                    >
                        <Input placeholder="e.g., keshika@bashyamgroup.com" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}